// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {DateInput, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {Form, ResponsiveType, useResponsive} from '@supermove/hooks';
import {OrganizationModel} from '@supermove/models';

// App
import FieldInput from '@shared/design/components/Field/FieldInput';
import {
  handleToggleEstimatedRange,
  JobDateFieldComponents,
} from 'modules/Job/components/JobDateFields';
import DateRangeInput from 'modules/Project/V2/components/DateRangeInput';

const EstimatedRowContainer = Styled.View`
`;

const SingleInputContainer = Styled.View`
  flex-direction: row;
  align-items: center;
`;
const ToggleContainer = Styled.View<{responsive: ResponsiveType}>`
  flex-direction: row;
  align-self: flex-start;
  padding-top: ${({responsive}) => (responsive.desktop ? 6 : 14)}px;
`;

const SingleDateInput = ({
  field,
  form,
  isRequired,
  organization,
  projectTypeId,
  isJobDateRequired,
}: {
  field: string;
  form: Form;
  isRequired: boolean;
  organization: OrganizationModel;
  projectTypeId: string;
  isJobDateRequired?: boolean;
}) => {
  const responsive = useResponsive();
  const {isEnabledEstimatedRangeJobs, isEnabledCreateProjectDispatchLocks} = organization.features;
  const dateFieldName = `${field}.date`;
  const date = _.get(form.values, dateFieldName);
  const projectType = organization.moveProjectTypes.find(
    (projectType) => projectType.id === _.toString(projectTypeId),
  );
  const jobTypeId = _.get(form.values, `${field}.jobTypeId`);
  const jobType = organization.jobTypes.find((jobType) => jobType.id === _.toString(jobTypeId));
  const hasDispatchLocks = jobType && !['ESTIMATE', 'REQUEST'].includes(jobType.kind);
  const excludeDates =
    projectType && isEnabledCreateProjectDispatchLocks && hasDispatchLocks
      ? projectType.futureSalesLockedProjectTypeDays.map(
          (projectTypeDay) => projectTypeDay.day.date,
        )
      : [];

  return (
    <SingleInputContainer responsive={responsive}>
      <FieldInput
        {...form}
        component={DateInput}
        name={dateFieldName}
        isResponsive
        input={{
          disabled: isJobDateRequired
            ? !isJobDateRequired
            : isEnabledCreateProjectDispatchLocks && !projectTypeId,
          required: (isJobDateRequired && !date) || (isRequired && !date),
          onChangeDate: () => {
            form.setTouched({...form.touched, [dateFieldName]: true});
          },
          placeholder: 'Enter date',
          setFieldValue: form.setFieldValue,
          style: {width: '100%'},
          excludeDates,
          isPortaled: true,
        }}
        style={{flex: 1}}
      />
      {isEnabledEstimatedRangeJobs ? (
        <ToggleContainer responsive={responsive}>
          <Space width={4} />
          <JobDateFieldComponents.EstimatedRangeToggle
            form={form}
            field={field}
            isEstimatedRange={false}
            isResponsive
          />
        </ToggleContainer>
      ) : null}
    </SingleInputContainer>
  );
};

const EstimatedRangeInputs = ({
  field,
  form,
  isRequired,
}: {
  field: string;
  form: Form;
  isRequired: boolean;
}) => {
  const startDateField = `${field}.startDate`;
  const startDate = _.get(form.values, startDateField) as string;
  const endDateField = `${field}.endDate`;
  const endDate = _.get(form.values, endDateField);

  return (
    <EstimatedRowContainer>
      <DateRangeInput
        form={form}
        field={field}
        startDateField={startDateField}
        endDateField={endDateField}
        toggleRangeOnSameDate={(startDate) => {
          handleToggleEstimatedRange({form, field, wasEstimatedRange: true, startDate});
        }}
        startDateProps={{isResponsive: true}}
        startDateInputProps={{
          placeholder: 'Start date',
          required: isRequired && !startDate,
          isPortaled: true,
        }}
        endDateProps={{isResponsive: true}}
        endDateInputProps={{
          placeholder: 'End date',
          required: isRequired && !endDate,
          isPortaled: true,
        }}
        hideWarning
      />
    </EstimatedRowContainer>
  );
};

const RowJobDateFields = ({
  field,
  form,
  organization,
  projectTypeId,
  isJobDateRequired,
}: {
  field: string;
  form: Form<any>;
  organization: OrganizationModel;
  projectTypeId: string;
  isJobDateRequired?: boolean;
}) => {
  const isEstimatedRange = _.get(form.values, `${field}.isEstimatedRange`);
  const isRequired = organization.features.isEnabledJobStartDateIsRequired;

  if (isEstimatedRange) {
    return <EstimatedRangeInputs field={field} form={form} isRequired={isRequired} />;
  }
  return (
    <SingleDateInput
      field={field}
      form={form}
      isRequired={isRequired}
      organization={organization}
      projectTypeId={projectTypeId}
      isJobDateRequired={isJobDateRequired}
    />
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
RowJobDateFields.fragment = gql`
  fragment RowJobDateFields on Organization {
    id
    features {
      isEnabledEstimatedRangeJobs: isEnabled(feature: "DATE_RANGE_JOBS")
      isEnabledJobStartDateIsRequired: isEnabled(feature: "JOB_START_DATE_IS_REQUIRED")
      isEnabledCreateProjectDispatchLocks: isEnabled(feature: "CREATE_PROJECT_DISPATCH_LOCKS")
    }
    moveProjectTypes: projectTypesForCategory(category: "MOVE") {
      id
      futureSalesLockedProjectTypeDays {
        id
        day {
          id
          date
        }
      }
    }
    jobTypes {
      id
      kind
    }
  }
`;

export default RowJobDateFields;
